import type { Runner } from "@/lib/api";

export const RUNNER_LIST_POLL_INTERVAL_MS = 10_000;

/** 更新を返したランナーが新しい版で起動し直して報告するのを待つ上限。過ぎても古い版なら「更新」を押し直せる。 */
export const RUNNER_UPDATE_RESTART_GRACE_MS = 120_000;

/**
 * 更新の結果を Izanagi が待ちきれなかった (504) ときに、ランナーが取得・読み込みを終えて新しい版を報告するのを待つ上限。
 * ランナーの読み込みの上限 (5 分) と起動し直しを含めた長さにする。
 */
export const RUNNER_UPDATE_SLOW_GRACE_MS = 600_000;

/** /api/runners の status (登録の段階)。承認前の登録要求は一覧に出ない。 */
export type RunnerRegistrationStatus = "approved" | "active" | "revoked";

/** 一覧に出すデバイスの状態。登録済みのデバイスはハートビートの内容で細かく分ける。 */
export type RunnerDisplayState =
	| "online"
	| "offline"
	| "preparing"
	| "prepareFailed"
	| "approved"
	| "revoked";

/** 登録の状態、接続の有無、準備の状態の順に判定し、一覧に表示する状態を返す。 */
export function runnerDisplayState(
	runner: Pick<Runner, "status" | "online" | "runnerStatus">,
): RunnerDisplayState {
	if (runner.status !== "active") return runner.status;
	if (!runner.online) return "offline";
	switch (runner.runnerStatus) {
		case "prepareFailed":
			return "prepareFailed";
		case "starting":
		case "preparing":
			return "preparing";
		default:
			return "online";
	}
}

export const RUNNER_STATE_LABELS: Record<RunnerDisplayState, string> = {
	online: "オンライン",
	offline: "オフライン",
	preparing: "準備中",
	prepareFailed: "準備に失敗",
	approved: "接続待ち",
	revoked: "解除済み",
};

const NEUTRAL = { background: "var(--surface-2)", color: "var(--fg-2)" };
const WARNING = { background: "var(--warning-subtle)", color: "var(--warning)" };
const DANGER = { background: "var(--danger-subtle)", color: "var(--danger)" };

export const RUNNER_STATE_THEMES: Record<
	RunnerDisplayState,
	{ background: string; color: string }
> = {
	online: { background: "var(--success-subtle)", color: "var(--success)" },
	offline: NEUTRAL,
	preparing: WARNING,
	prepareFailed: DANGER,
	approved: WARNING,
	revoked: NEUTRAL,
};

/** ランナーが最低の版より古く、更新が必要なときの案内の前半。 */
export const RUNNER_UPDATE_REQUIRED_REASON =
	"デバイスにインストールされているローカルデバイス版 Izanagi のバージョンを更新できます。";

/** 空いているライセンスが無いときの、ダウンロード・登録用のコードの発行をさせない理由。backend の文言と揃える。 */
export function noFreeDeviceLicenseMessage(licenseCount: number): string {
	if (licenseCount === 0)
		return "デバイスのライセンスがありません。ライセンスの追加は Monomyth にお問い合わせください。";
	return `空いているライセンスがありません (ライセンス ${licenseCount} 台)。ほかのデバイスを解除するか、ライセンスの追加を Monomyth にお問い合わせください。`;
}

export function canRevokeRunner(runner: Pick<Runner, "status">): boolean {
	return runner.status !== "revoked";
}

type RunnerOperationTarget = Pick<Runner, "status" | "online">;

/** 操作の命令を受け付けるランナーにつながっている。 */
function isConnected(runner: RunnerOperationTarget): boolean {
	return runner.status === "active" && runner.online;
}

/** 「更新」: ランナーが配る版より古く (backend が判定)、オンラインのときだけ。 */
export function canUpdateRunner(
	runner: RunnerOperationTarget & Pick<Runner, "updateAvailable">,
): boolean {
	return isConnected(runner) && runner.updateAvailable;
}

/** 「再試行」: 環境イメージの取得に失敗して、自動の試し直しを待っているときだけ。 */
export function canRetryRunnerPrepare(
	runner: RunnerOperationTarget & Pick<Runner, "runnerStatus">,
): boolean {
	return isConnected(runner) && runner.runnerStatus === "prepareFailed";
}

const USER_CODE_LETTERS = 8;
const USER_CODE_GROUP = 4;

/**
 * 入力を確認コードの表示 (XXXX-XXXX) に整える。大文字小文字・全角・ハイフンや空白の有無は問わず、
 * 英字だけを 8 文字まで残す。
 */
export function formatRunnerUserCode(input: string): string {
	const letters = input
		.normalize("NFKC")
		.toUpperCase()
		.replace(/[^A-Z]/g, "")
		.slice(0, USER_CODE_LETTERS);
	return letters.length > USER_CODE_GROUP
		? `${letters.slice(0, USER_CODE_GROUP)}-${letters.slice(USER_CODE_GROUP)}`
		: letters;
}

/** 正規化した入力が確認コード 8 文字分を含むかを返す。コードの存在や有効期限は確認しない。 */
export function isCompleteRunnerUserCode(code: string): boolean {
	return formatRunnerUserCode(code).length === USER_CODE_LETTERS + 1;
}
